import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { Company } from 'identity-webapi-client';
import { Subscription } from 'rxjs';
import { animate, keyframes, transition, trigger } from '@angular/animations';
import { ArtifactVersion, DeleteEnvironmentOverrideRequest, EnvironmentSettings, ListEnvironmentsByBuilderRequest, SituRenderServiceWebApiClient } from 'siturenderservice-webapi-client';
import { EnvironmentName, ProductName } from 'situview-webapi-client';
import { AuthService, BreadcrumbService, IProductNameConfiguration, ProductNameService, StateService } from 'src/app/services';
import { Role } from 'src/app/shared/models/role.model';
import * as kf from '../../../core/keyframes';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-environments-list',
  templateUrl: './environments-list.component.html',
  styleUrls: ['./environments-list.component.scss'],
  animations: [
    trigger('cardAnimator', [
      transition('* => headShake', animate(1000, keyframes(kf.headShake)))
    ])
  ]
})
export class EnvironmentsListComponent implements OnInit, OnDestroy {
  selectedProduct: IProductNameConfiguration | undefined;
  products: IProductNameConfiguration[] | undefined;

  selectedCompany: Company | null = null;
  productArtifactKeyOverrides: [] = [];
  environments: EnvironmentSettings[] = [];
  selectedEnvironment: EnvironmentSettings | undefined;
  admin = false;
  allowEdit = false;

  EnvironmentNameEnum = EnvironmentName;

  animateAddOverride: string = '';
  overridesLoading: boolean = false;
  filteredOverrides: ArtifactVersion[] | undefined;
  searchField = new FormControl();
  expandedOverridesList: boolean = false;

  private _subscriptions: Subscription[] = [];

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private situRenderServiceClient: SituRenderServiceWebApiClient,
    private authService: AuthService,
    private stateService: StateService,
    private breadcrumbService: BreadcrumbService,
    private productNameService: ProductNameService) { }

  ngOnInit(): void {
    this.admin = (this.authService.hasRole(Role.SituSystemsDeveloper) || this.authService.hasRole(Role.SituSystemsAdmin));
    this.products = [
      ...this.productNameService.productNameConfigurations,
      this.productNameService.getVanityConfiguration(),
      this.productNameService.getInsightsConfiguration(),
      this.productNameService.getUnrealConfiguration(),
      this.productNameService.getConnectConfiguration()
    ];

    // set selectedProduct    
    if (this.route.snapshot.queryParamMap.get('product')) {
      let productParam = ProductName[this.route.snapshot.queryParamMap.get('product') as keyof typeof ProductName];
      if (productParam) {
        this.selectedProduct = this.productNameService.getProductNameConfiguration(productParam as ProductName);
        if (this.selectedProduct) {
          this.productNameService.productNameConfiguration = this.selectedProduct;
        }
      }
    }
    if (!this.selectedProduct) {
      this.selectedProduct = this.productNameService.productNameConfiguration;
      this.router.navigate(['/admin/environments'], {
        queryParams: {
          product: ProductName[this.selectedProduct.productName]
        },
        queryParamsHandling: 'merge'
      });
    }

    this._subscriptions.push(this.stateService.state$.subscribe((state) => {
      let company = state.company as Company ?? undefined
      if (this.selectedCompany?.id !== company.id) {
        this.selectedCompany = state.company as Company ?? undefined;

        this.admin = (this.authService.hasRole(Role.SituSystemsDeveloper) || this.authService.hasRole(Role.SituSystemsAdmin));
        this.allowEdit = this.authService.hasAllRoles([Role.SituSystemsDeveloper, Role.SituSystemsAdmin]);

        this.loadEnvironments();
      }
    }));
  }

  ngOnDestroy(): void {
    if (this._subscriptions) {
      for (let sub of this._subscriptions) {
        sub.unsubscribe();
      }
      this._subscriptions = [];
    }
  }

  loadEnvironments() {
    var company = this.selectedCompany;

    if (company === undefined || company === null) {
      return;
    }

    this.loading = true;

    var request = new ListEnvironmentsByBuilderRequest();
    request.builder = company.code;
    request.product = this.selectedProduct?.productName;

    this.situRenderServiceClient.listEnvironmentsByBuilder(request).then((response: any) => {
      if (response.success) {
        this.environments = response.environments ?? [];
        this.environments.forEach(x => x.referenceKeyCuttoffs ?? []);

        // set selectedEnvironment
        if (this.route.snapshot.queryParamMap.get('environment')) {
          let environmentParam = EnvironmentName[this.route.snapshot.queryParamMap.get('environment') as keyof typeof EnvironmentName];
          if (environmentParam) {
            this.selectedEnvironment = this.environments.find(x => x.environment?.environment == environmentParam);
          }
        }
        if (!this.selectedEnvironment && this.environments.length > 0) {
          this.selectedEnvironment = this.environments[0];
          this.router.navigate(['/admin/environments'], {
            queryParams: {
              environment: EnvironmentName[this.selectedEnvironment.environment!.environment!]
            },
            queryParamsHandling: 'merge'
          });
        }
      }
      else {
        this.environments = [];
        this.selectedEnvironment = undefined;
      }

      this.filteredOverrides = this.selectedEnvironment?.overrides ?? [];
      if (this.selectedEnvironment?.overrides?.length === 0) {
        this.startAnimation("headShake");
      }

      this.loading = false;
    }, (error: any) => {
      this.loading = false;
    });
  }

  productChange(event: MatSelectChange) {
    if (event === undefined) return;

    this.selectedProduct = event.value;
    this.productNameService.productNameConfiguration = event.value;
    this.router.navigate(['/admin/environments'], {
      queryParams: {
        product: ProductName[this.selectedProduct!.productName]
      },
      queryParamsHandling: 'merge'
    });
    this.loadEnvironments();
  }

  environmentChange(event: MatSelectChange) {
    if (event === undefined) return;

    this.selectedEnvironment = event.value;
    this.router.navigate(['/admin/environments'], {
      queryParams: {
        environment: EnvironmentName[this.selectedEnvironment!.environment!.environment!]
      },
      queryParamsHandling: 'merge'
    });

    this.filteredOverrides = this.selectedEnvironment?.overrides ?? [];
    if (this.selectedEnvironment?.overrides?.length === 0) {
      this.startAnimation("headShake");
    }
  }

  startAnimation(state: string) {
    if (!this.animateAddOverride) {
      this.animateAddOverride = state;
    }
  }
  resetAddOverrideAnimationState() {
    this.animateAddOverride = '';
  }
  onClickAddOverride() {
    this.router.navigate([`/admin/environments/add-override`], {
      queryParams: {
        warpEnvironmentId: this.selectedEnvironment?.id,
      },
      queryParamsHandling: 'merge'
    });
    return;
  }
  onClickEditOverride(overrideId: string) {
    this.router.navigate([`/admin/environments/add-override`], {
      queryParams: {
        warpEnvironmentId: this.selectedEnvironment?.id,
        overrideId: overrideId
      },
      queryParamsHandling: 'merge'
    });
    return;
  }
  onClickDeleteOverride(overrideId: string) {
    if (confirm("Are you sure you want to delete this override?")) {
      let request = new DeleteEnvironmentOverrideRequest();
      request.environmentOverrideId = overrideId;

      this.situRenderServiceClient.deleteEnvironmentOverride(request)
        .then(response => {
          if (response.success) {
            this.showMessage(`Override deleted.`);
            this.loadEnvironments();
          } else {
            this.showMessage(`Sorry we were unable to delete your override. Please try again!`);
          }
        })
        .catch((x: any) => {
          this.showMessage(`Sorry we were unable to delete your override. Please try again!`);
        });
    }
  }
  toggleExpandedOverridesList() {
    this.expandedOverridesList = !this.expandedOverridesList;
  }
  doFilter = (value: string) => {
    if (value) {
      let searchTerm = value.trim().toLocaleLowerCase();
      this.filteredOverrides = this.selectedEnvironment?.overrides?.filter(e => { return e.callerReferenceKey?.toLocaleLowerCase().includes(searchTerm) });
    } else {
      this.filteredOverrides = this.selectedEnvironment?.overrides;
    }
  }
  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}
